'use client';

import type { BoxProps } from '@mui/material/Box';

import { forwardRef } from 'react';

import Box from '@mui/material/Box';

import { RouterLink } from 'src/routes/components';

import { logoClasses } from './classes';

// ----------------------------------------------------------------------

export type LogoProps = BoxProps & {
  isSingle?: boolean;
  disableLink?: boolean;
  isFaq?: boolean;
  fillId?: string;
};

export const Logo = forwardRef<HTMLDivElement, LogoProps>(
  (
    {
      isFaq,
      width,
      height,
      isSingle = true,
      disableLink = false,
      className,
      fillId = 'logo-gradient-default',
      sx,
      ...other
    },
    ref
  ) => {
    const singleLogo = (
      <svg
        width="100%"
        height="100%"
        viewBox="0 0 93 79"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M74.4675 0.863037C65.6165 0.863037 51.3127 12.7529 51.3127 21.7259C51.3127 24.1612 53.2644 25.9075 57.1186 25.9075C60.9021 25.9075 70.0463 23.9515 70.0463 33.9089C70.0463 37.3565 68.101 40.5108 65.1799 40.5108C53.9492 40.5108 32.072 0.863037 17.7939 0.863037C9.2061 0.863037 0.795898 19.4917 0.795898 39.5564C0.795898 63.1392 7.82152 78.2497 18.5857 78.2497C27.4368 78.2497 41.7405 66.3599 41.7405 57.3868C41.7405 54.9515 39.7888 53.2053 35.9347 53.2053C32.1512 53.2053 23.007 55.1613 23.007 45.2038C23.007 41.7563 24.9522 38.6019 27.8733 38.6019C39.104 38.6019 60.9813 78.2497 75.2593 78.2497C83.8472 78.2497 92.2552 59.621 92.2552 39.5564C92.2574 15.9757 85.2317 0.863037 74.4675 0.863037Z"
          fill={`url(#${fillId})`}
        />
        <defs>
          <linearGradient
            id={fillId}
            x1="0.800178"
            y1="39.5573"
            x2="92.2574"
            y2="39.5573"
            gradientUnits="userSpaceOnUse"
          >
            <stop offset="1.4385e-07" stopColor="#FF7F4A" />
            <stop offset="0.3045" stopColor="#FE3EA1" />
            <stop offset="0.6173" stopColor="#883FFF" />
            <stop offset="1" stopColor="#4ECAFE" />
          </linearGradient>
        </defs>
      </svg>
    );

    const fullLogo = (
      <svg
        width="100%"
        height="100%"
        viewBox="0 0 93 79"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M74.4675 0.863037C65.6165 0.863037 51.3127 12.7529 51.3127 21.7259C51.3127 24.1612 53.2644 25.9075 57.1186 25.9075C60.9021 25.9075 70.0463 23.9515 70.0463 33.9089C70.0463 37.3565 68.101 40.5108 65.1799 40.5108C53.9492 40.5108 32.072 0.863037 17.7939 0.863037C9.2061 0.863037 0.795898 19.4917 0.795898 39.5564C0.795898 63.1392 7.82152 78.2497 18.5857 78.2497C27.4368 78.2497 41.7405 66.3599 41.7405 57.3868C41.7405 54.9515 39.7888 53.2053 35.9347 53.2053C32.1512 53.2053 23.007 55.1613 23.007 45.2038C23.007 41.7563 24.9522 38.6019 27.8733 38.6019C39.104 38.6019 60.9813 78.2497 75.2593 78.2497C83.8472 78.2497 92.2552 59.621 92.2552 39.5564C92.2574 15.9757 85.2317 0.863037 74.4675 0.863037Z"
          fill="url(#paint0_linear_2613_7780)"
        />
        <defs>
          <linearGradient
            id="paint0_linear_2613_7780"
            x1="0.800178"
            y1="39.5573"
            x2="92.2574"
            y2="39.5573"
            gradientUnits="userSpaceOnUse"
          >
            <stop offset="1.4385e-07" stopColor="#FF7F4A" />
            <stop offset="0.3045" stopColor="#FE3EA1" />
            <stop offset="0.6173" stopColor="#883FFF" />
            <stop offset="1" stopColor="#4ECAFE" />
          </linearGradient>
        </defs>
      </svg>
    );

    const baseSize = {
      width: width ?? 40,
      height: height ?? 40,
      ...(!isSingle && {
        width: width ?? 102,
        height: height ?? 36,
      }),
    };

    return (
      <Box
        ref={ref}
        component={RouterLink}
        href={isFaq ? '/faqs' : '/center-app'}
        className={logoClasses.root.concat(className ? ` ${className}` : '')}
        aria-label="Logo"
        sx={{
          ...baseSize,
          flexShrink: 0,
          display: 'inline-flex',
          verticalAlign: 'middle',
          ...(disableLink && { pointerEvents: 'none' }),
          ...sx,
        }}
        {...other}
      >
        {isSingle ? singleLogo : fullLogo}
      </Box>
    );
  }
);
